/**
 * useEditMode — Edit mode enter/apply/cancel/dismiss + split content management
 *
 * Extracted from MainWorkspace to reduce component size.
 * Manages: entering edit mode, applying changes (with override write-through),
 * cancelling edits, dismissing split view, and clearing stale split content.
 *
 * @module @humanizer/studio/hooks/useEditMode
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type { ContentItem } from '../contexts/BufferSyncContext';
import type { WorkspaceContent, SplitContent, ViewMode } from '../components/workspace/workspace-types';
import { stripPandocExtensions } from '../utils/markdown-utils';
import { createLogger } from '../utils/debug';

const log = createLogger('Workspace');

export interface UseEditModeOptions {
  /** Active content item */
  content: WorkspaceContent | null | undefined;
  /** Current edit content text */
  editContent: string;
  /** Set edit content */
  setEditContent: (text: string) => void;
  /** Current view mode */
  viewMode: ViewMode;
  /** Set view mode */
  setViewMode: (mode: ViewMode) => void;
  /** Whether thread mode is active */
  isThreadMode: boolean;
  /** Messages array */
  messages: ContentItem[] | undefined;
  /** Active message index */
  activeMessageIndex: number;
  /** Current node ID for override write-through */
  currentNodeId: string | undefined;
  /** Set override flag after write-through */
  setHasOverride: (v: boolean) => void;
  /** Buffer sync setContent */
  setContent: (items: ContentItem[]) => Promise<void>;
  /** Buffer sync commit */
  commit: (message: string) => Promise<void>;
  /** API overrides service */
  api: { overrides: { save: (nodeId: string, original: string, replacement: string) => Promise<unknown> } };
  /** Whether archive is local */
  isLocalArchive: boolean;
  /** Split content (external trigger from TransformTool) */
  splitContent: SplitContent | null | undefined;
  /** Callback when split is dismissed */
  onSplitDismissed?: () => void;
  /** Toast notifications */
  addToast: (message: string, type: 'success' | 'info' | 'warning') => void;
}

export interface UseEditModeReturn {
  isSaving: boolean;
  handleEnterEdit: () => void;
  handleApplyChanges: () => Promise<void>;
  handleCancelEdit: () => void;
  handleDismissSplit: () => void;
}

export function useEditMode({
  content,
  editContent,
  setEditContent,
  viewMode,
  setViewMode,
  isThreadMode,
  messages,
  activeMessageIndex,
  currentNodeId,
  setHasOverride,
  setContent,
  commit,
  api,
  isLocalArchive,
  splitContent,
  onSplitDismissed,
  addToast,
}: UseEditModeOptions): UseEditModeReturn {
  const [isSaving, setIsSaving] = useState(false);

  // Sync edit content when navigating to different content while in edit mode
  const prevContentId = useRef(content?.id);
  const prevMessageIdx = useRef(activeMessageIndex);
  useEffect(() => {
    const contentChanged = content?.id !== prevContentId.current;
    const messageChanged = activeMessageIndex !== prevMessageIdx.current;
    prevContentId.current = content?.id;
    prevMessageIdx.current = activeMessageIndex;

    if (viewMode === 'edit' && (contentChanged || messageChanged)) {
      const text = isThreadMode && messages && messages[activeMessageIndex]
        ? messages[activeMessageIndex].text
        : content?.text ?? '';
      setEditContent(text);
    }
  }, [content?.id, activeMessageIndex, isThreadMode, messages, viewMode, content, setEditContent]);

  const handleEnterEdit = useCallback(() => {
    let text = isThreadMode && messages && messages[activeMessageIndex]
      ? messages[activeMessageIndex].text
      : content?.text ?? '';

    // Strip Pandoc extensions for markdown content to give clean GFM for editing
    const currentItem = isThreadMode && messages ? messages[activeMessageIndex] : null;
    const isMarkdownContent = currentItem?.type === 'archive-node'
      || currentItem?.metadata?.format === 'markdown'
      || content?.source?.type === 'archive';
    if (isMarkdownContent) {
      text = stripPandocExtensions(text);
    }

    setEditContent(text);
    setViewMode('edit');
  }, [isThreadMode, messages, activeMessageIndex, content, setEditContent, setViewMode]);

  const handleApplyChanges = useCallback(async () => {
    if (!content) return;

    setIsSaving(true);
    try {
      if (isThreadMode && messages) {
        const updatedMessages = messages.map((m, i) =>
          i === activeMessageIndex
            ? { ...m, text: editContent, metadata: { ...m.metadata, wordCount: editContent.split(/\s+/).filter(Boolean).length, hasOverride: !!m.metadata.source?.nodeId } }
            : m
        );
        await setContent(updatedMessages);

        // Write-through to override table
        const msg = messages[activeMessageIndex];
        const nodeId = msg?.metadata.source?.nodeId;
        if (nodeId) {
          if (isLocalArchive && window.electronOverride) {
            try {
              await window.electronOverride.save(nodeId, msg.text, editContent);
              setHasOverride(true);
            } catch (err) {
              log.error('Failed to save local override:', err);
            }
          } else if (!isLocalArchive) {
            try {
              await api.overrides.save(nodeId, msg.text, editContent);
              setHasOverride(true);
            } catch (err) {
              log.error('Failed to save override:', err);
            }
          }
        }
      } else {
        await setContent([{
          id: content.id,
          type: 'text',
          text: editContent,
          metadata: {
            wordCount: editContent.split(/\s+/).filter(Boolean).length,
            authorRole: (content.metadata?.authorRole as 'user' | 'assistant' | 'system' | 'tool' | undefined),
          },
        }]);

        // Write-through to override table
        if (currentNodeId) {
          if (isLocalArchive && window.electronOverride) {
            try {
              await window.electronOverride.save(currentNodeId, content.text ?? '', editContent);
              setHasOverride(true);
            } catch (err) {
              log.error('Failed to save local override:', err);
            }
          } else if (!isLocalArchive) {
            try {
              await api.overrides.save(currentNodeId, content.text ?? '', editContent);
              setHasOverride(true);
            } catch (err) {
              log.error('Failed to save override:', err);
            }
          }
        }
      }
      await commit('Edit in workspace');
      setViewMode('read');
    } catch (error) {
      log.error('Failed to save changes:', error);
      addToast('Failed to save changes', 'warning');
    } finally {
      setIsSaving(false);
    }
  }, [content, editContent, setContent, commit, isThreadMode, messages, activeMessageIndex, api.overrides, currentNodeId, isLocalArchive, addToast, setHasOverride, setViewMode]);

  const handleCancelEdit = useCallback(() => {
    setViewMode('read');
  }, [setViewMode]);

  const handleDismissSplit = useCallback(() => {
    setViewMode('read');
    onSplitDismissed?.();
  }, [onSplitDismissed, setViewMode]);

  // Clear stale split content when navigating to a different buffer.
  // Single effect to avoid race: capture origin on first activation,
  // dismiss when content changes while split is active.
  const splitOriginIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (splitContent) {
      if (splitOriginIdRef.current === null) {
        // First time split is set — capture origin
        splitOriginIdRef.current = content?.id ?? null;
      } else if (content?.id && content.id !== splitOriginIdRef.current) {
        // Content changed while split is active — dismiss
        setViewMode('read');
        onSplitDismissed?.();
      }
    } else {
      // Split cleared — reset tracking
      splitOriginIdRef.current = null;
    }
  }, [splitContent, content?.id, onSplitDismissed, setViewMode]);

  return {
    isSaving,
    handleEnterEdit,
    handleApplyChanges,
    handleCancelEdit,
    handleDismissSplit,
  };
}
